import type { GameState, OfflineReport, ResourceId } from './types';
import { BALANCE, RESOURCE_IDS } from './content';
import { collectModifiers } from './modifiers';
import { MAX_TICK_SECONDS, advance } from './tick';

/** Hours of away time the tribe can carry on for, including ancestral bonuses. */
export function offlineCapSeconds(state: GameState): number {
  return collectModifiers(state).offlineHours * 3600;
}

/**
 * Simulate time spent away in coarse steps. Returns null when the absence was
 * too short to be worth reporting.
 */
export function simulateOffline(
  state: GameState,
  now: number = Date.now(),
): OfflineReport | null {
  const elapsedSeconds = Math.max(0, (now - state.lastTick) / 1000);
  state.lastTick = now;

  // Under a minute is just a page refresh; fold it into the normal tick loop.
  if (elapsedSeconds < 60) return null;

  const cappedSeconds = Math.min(elapsedSeconds, offlineCapSeconds(state));

  const resourcesBefore: Record<ResourceId, number> = {};
  for (const resourceId of RESOURCE_IDS) {
    resourcesBefore[resourceId] = state.resources[resourceId] ?? 0;
  }
  const populationBefore = state.population;
  const talesBefore = state.stats.totalTalesSeen;

  // advance() clamps a single call to MAX_STEP_SECONDS, so the step size must
  // stay under it or a very long absence would silently lose time.
  const steps = Math.min(
    BALANCE.offlineMaxSteps,
    Math.ceil(cappedSeconds / BALANCE.offlineStepSeconds),
  );
  const stepSize = steps > 0 ? Math.min(MAX_TICK_SECONDS, cappedSeconds / steps) : 0;

  let simulated = 0;
  for (let step = 0; step < steps && simulated < cappedSeconds; step += 1) {
    const thisStep = Math.min(stepSize, cappedSeconds - simulated);
    advance(state, thisStep);
    simulated += thisStep;
  }

  const gained: Record<ResourceId, number> = {};
  for (const resourceId of RESOURCE_IDS) {
    const delta = (state.resources[resourceId] ?? 0) - (resourcesBefore[resourceId] ?? 0);
    if (Math.abs(delta) > 1e-6) gained[resourceId] = delta;
  }

  return {
    elapsedSeconds,
    cappedSeconds,
    gained,
    populationChange: state.population - populationBefore,
    talesSeen: state.stats.totalTalesSeen - talesBefore,
  };
}
